import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import { useConversationEditUserSetting } from '../../../modules/apis/student/conversation'
import { useCustomerType } from '../../../modules/apis/student/products'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_speaking, n_ai_tutor, n_common, s_landing_pricing } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { timeDifference } from '../../../modules/utils'
import { useConversationAIStack } from '../../../pages/[lang]/webview/conversational-ai'

export const handleCheckConversationPopupButton = (
  conversationCreditsSummaryData,
  conversationThreadsSummaryData,
  setNonCreditModal,
  setContinueConversationModal,
  setNewConversationModal
) => {
  if (!conversationCreditsSummaryData || !conversationThreadsSummaryData) return

  if (conversationCreditsSummaryData?.usable_credit_count == 0) {
    setNonCreditModal(true)
    return
  }

  if (conversationThreadsSummaryData.in_progress_thread_count > 0) {
    setContinueConversationModal(true)
    return
  } else {
    setNewConversationModal(true)
    return
  }
}

export const ConversationErrorPopup = ({ showErrorModal, setShowErrorModal }) => {
  const { t } = useTranslation()
  const getFullUrl = useReturnFullUrl()
  const router = useAppRouter({ browserDetect: true })

  const onClickChannelTalk = () => {
    console.log('onClickChannelTalk')
    router.push(urls.webview.helpCenter.showMessageger, {
      appScreenName: APP_SCREEN_NAME.OUTLINK_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.helpCenter.showMessageger), header: false },
    })
  }

  const reloadScreen = () => {
    setShowErrorModal(false)
    window?.location.reload()
  }

  return (
    <DSModal
      id="conversationErrorModal"
      isOpen={showErrorModal}
      setIsOpen={setShowErrorModal}
      size={'sm'}
      closeButton={false}
    >
      <DSModal.Title className="keep-all">{t(n_ai_speaking.popup_error.title)}</DSModal.Title>
      <DSModal.Content className="mt-[16px]">{t(n_ai_speaking.popup_error.body)}</DSModal.Content>
      <div onClick={() => onClickChannelTalk()}>
        <DSModal.Content className="text-purple underline cursor-pointer">
          {t(s_landing_pricing.ask_channeltalk)}
        </DSModal.Content>
      </div>

      <DSModal.ButtonContainer direction="horizontal">
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.SOLID} onClick={() => reloadScreen()}>
          <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

export const ConversationStopPopup = ({ thread, stopModal, setStopModal, minimumWord }) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { data: customerType } = useCustomerType()
  const getFullUrl = useReturnFullUrl()
  const { useConversationAIFlow } = useConversationAIStack()
  const { pop, push } = useConversationAIFlow()
  const router = useAppRouter({ stackPush: push, browserDetect: true })

  const diff = timeDifference(thread?.created_at)
  const requiresAttendanceVerification = customerType?.requiresAttendanceVerification

  const onClickExitThread = () => {
    router.push(urls.webview.aiTutor.index(null, 2), {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.aiTutor.index(null, 2)) },
    })

    // old version
    // router.push(urls.webview.conversationalAI.list, {
    //   appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
    //   appSendData: { url: getFullUrl(urls.webview.conversationalAI.list) },
    // })
  }

  return (
    <DSModal id="conversationErrorModal" isOpen={stopModal} setIsOpen={setStopModal} size={'sm'} closeButton={false}>
      <DSModal.Title className="keep-all">
        {requiresAttendanceVerification ? t(n_ai_speaking.popup_exit1.title_b2b) : t(n_ai_speaking.popup_exit1.title)}
      </DSModal.Title>
      <DSModal.Content className="mb-[24px]">
        {requiresAttendanceVerification
          ? minimumWord
            ? t(n_ai_speaking.b2b_wordcount.popup_body2(minimumWord, diff.minutes))
            : t(n_ai_speaking.popup_exit1.body_b2b(diff.minutes))
          : t(n_ai_speaking.popup_exit1.body(diff.minutes))}
      </DSModal.Content>
      <DSModal.ButtonContainer direction="horizontal">
        {/* 팝업만 끄기 */}
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.OUTLINE} onClick={() => setStopModal(false)}>
          <DSButton.Label>{t(n_ai_tutor.roleplay_popup.btn1)}</DSButton.Label>
        </DSButton>
        {/* 페이지 나가버리기 */}
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.SOLID} onClick={() => onClickExitThread()}>
          <DSButton.Label>{t(n_ai_tutor.roleplay_popup.btn2)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

export const ConversationCompletePopup = ({ showCompleteModal, setShowCompleteModal, onClickThreadSummary }) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()

  return (
    <DSModal
      id="conversationErrorModal"
      isOpen={showCompleteModal}
      setIsOpen={setShowCompleteModal}
      size={'sm'}
      closeButton={false}
    >
      <DSModal.Title className="keep-all">{t(n_ai_tutor.roleplay_popup.title1)}</DSModal.Title>
      <DSModal.Content className="">{t(n_ai_tutor.roleplay_popup.body1)}</DSModal.Content>

      <DSModal.ButtonContainer direction="horizontal">
        {/* 팝업만 끄기 */}
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.OUTLINE} onClick={() => setShowCompleteModal(false)}>
          <DSButton.Label>{t(n_ai_tutor.roleplay_popup.btn1)}</DSButton.Label>
        </DSButton>
        {/* 세션 요약하기 */}
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.SOLID} onClick={() => onClickThreadSummary()}>
          <DSButton.Label>{t(n_ai_tutor.roleplay_popup.btn2)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

// B2B/멀캠 일회성으로 뜨는 팝업
export const ConversationB2BTempPopup = ({
  showConversationB2BTempPopup,
  setShowConversationB2BTempPopup,
  minimumWord,
}) => {
  const { t } = useTranslation()
  const { mutate: edit } = useConversationEditUserSetting()

  const onClickOk = () => {
    edit(
      { key: 'hide_conversation_b2b_temp_popup', value: true },
      {
        onSuccess: () => {
          setShowConversationB2BTempPopup(false)
        },
      }
    )
  }

  return (
    <DSModal
      id="ConversationB2BTempPopup"
      isOpen={showConversationB2BTempPopup}
      setIsOpen={setShowConversationB2BTempPopup}
      size={'sm'}
      closeButton={false}
    >
      <DSModal.Title className="keep-all">{t(n_ai_speaking.b2b_wordcount.popup_title)}</DSModal.Title>
      <DSModal.Content className="">
        {minimumWord
          ? t(n_ai_speaking.b2b_wordcount.popup_body1(minimumWord))
          : t(n_ai_speaking.b2b_wordcount.popup_nolimit)}
      </DSModal.Content>

      <DSModal.ButtonContainer direction="horizontal">
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.SOLID} onClick={() => onClickOk()}>
          <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

export const ConversationRoleplayTempPopup = ({
  title,
  content,
  showRoleplayInstruction,
  setShowRoleplayInstruction,
  startRoleplay,
}) => {
  const { t } = useTranslation()

  const onClickOk = () => {
    setShowRoleplayInstruction(false)
    startRoleplay()
  }

  return (
    <DSModal
      id="ConversationRoleplayTempPopup"
      isOpen={showRoleplayInstruction}
      setIsOpen={setShowRoleplayInstruction}
      size={'sm'}
      closeButton={false}
    >
      <DSModal.Title className="keep-all">{title}</DSModal.Title>
      <DSModal.Content className="">{content}</DSModal.Content>

      <DSModal.ButtonContainer direction="horizontal">
        <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.SOLID} onClick={() => onClickOk()}>
          <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}
